import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, UserPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

export interface EmergencyContact {
  id?: string;
  contact_name: string;
  contact_phone: string;
  relationship: string;
  address?: string;
  is_primary: boolean;
}

interface EmergencyContactSectionProps {
  contacts: EmergencyContact[];
  onChange: (contacts: EmergencyContact[]) => void;
}

const RELATIONSHIPS = [
  "أب",
  "أم",
  "زوج/زوجة",
  "أخ",
  "أخت",
  "ابن",
  "ابنة",
  "صديق",
  "قريب آخر"
];

export const EmergencyContactSection = ({ contacts, onChange }: EmergencyContactSectionProps) => {
  const [expandedContact, setExpandedContact] = useState<number | null>(0);

  const addContact = () => {
    const newContact: EmergencyContact = {
      contact_name: "",
      contact_phone: "",
      relationship: "",
      address: "",
      is_primary: contacts.length === 0
    };
    onChange([...contacts, newContact]);
    setExpandedContact(contacts.length);
  };

  const removeContact = (index: number) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    // إذا تم حذف جهة الاتصال الأساسية، جعل الأولى أساسية
    if (contacts[index]?.is_primary && updatedContacts.length > 0) {
      updatedContacts[0].is_primary = true;
    }
    onChange(updatedContacts);
    setExpandedContact(null);
  };

  const updateContact = (index: number, field: keyof EmergencyContact, value: any) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    
    // إذا تم تعيين جهة اتصال كأساسية، إلغاء الأساسية من الباقي
    if (field === 'is_primary' && value === true) {
      updatedContacts.forEach((contact, i) => {
        if (i !== index) contact.is_primary = false;
      });
    }
    
    onChange(updatedContacts);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">بيانات الطوارئ</h3>
          <p className="text-sm text-muted-foreground">
            أضف جهات اتصال للحالات الطارئة
          </p>
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          onClick={addContact}
        >
          <UserPlus className="w-4 h-4 ml-2" />
          إضافة جهة اتصال
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <UserPlus className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">
            لا توجد جهات اتصال للطوارئ
          </p>
          <Button 
            type="button"
            variant="ghost" 
            onClick={addContact}
            className="mt-2"
          >
            <Plus className="w-4 h-4 ml-2" />
            أضف جهة اتصال
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedContact(expandedContact === index ? null : index)}
                  >
                    {contact.is_primary && (
                      <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded ml-2">
                        أساسي
                      </span>
                    )}
                    <span className="font-medium">
                      {contact.contact_name || `جهة اتصال ${index + 1}`}
                    </span>
                  </Button>
                </div>
                
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeContact(index)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {expandedContact === index && (
                <>
                  <Separator />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`contact_name_${index}`}>الاسم *</Label>
                      <Input
                        id={`contact_name_${index}`}
                        value={contact.contact_name}
                        onChange={(e) => updateContact(index, 'contact_name', e.target.value)}
                        placeholder="اسم جهة الاتصال"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`contact_phone_${index}`}>رقم الهاتف *</Label>
                      <Input
                        id={`contact_phone_${index}`}
                        value={contact.contact_phone}
                        onChange={(e) => updateContact(index, 'contact_phone', e.target.value)}
                        placeholder="+966 5X XXX XXXX"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`relationship_${index}`}>العلاقة *</Label>
                      <Select
                        value={contact.relationship}
                        onValueChange={(value) => updateContact(index, 'relationship', value)}
                      >
                        <SelectTrigger id={`relationship_${index}`}>
                          <SelectValue placeholder="اختر العلاقة" />
                        </SelectTrigger>
                        <SelectContent>
                          {RELATIONSHIPS.map((rel) => (
                            <SelectItem key={rel} value={rel}>
                              {rel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 flex items-end">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <input
                          type="checkbox"
                          id={`is_primary_${index}`}
                          checked={contact.is_primary}
                          onChange={(e) => updateContact(index, 'is_primary', e.target.checked)}
                          className="rounded"
                        />
                        <Label htmlFor={`is_primary_${index}`} className="cursor-pointer">
                          جهة اتصال أساسية
                        </Label>
                      </div>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor={`address_${index}`}>العنوان</Label>
                      <Textarea
                        id={`address_${index}`}
                        value={contact.address || ""}
                        onChange={(e) => updateContact(index, 'address', e.target.value)}
                        placeholder="عنوان جهة الاتصال"
                        rows={2}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
